// copied from vuetify
import type { VNode } from 'vue';

type ObserveHandler = (
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
  isIntersecting: boolean,
) => void;

interface ObserveVNodeDirective {
  value?:
    | ObserveHandler
    | { handler: ObserveHandler; options?: IntersectionObserverInit; postpone?: Promise<any> };
  modifiers?: {
    once?: boolean;
    quiet?: boolean;
  };
}

interface ObserveData {
  init: boolean;
  observer: IntersectionObserver;
}

interface HTMLElementWithObserve extends HTMLElement {
  _observe?: Record<number, ObserveData>;
}

async function inserted(el: HTMLElement, binding: ObserveVNodeDirective, vnode: VNode) {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

  const elementWithObserve = el as HTMLElementWithObserve;
  const uid = vnode.appContext?.app._uid ?? 0;

  const modifiers = binding.modifiers || {};
  const value = binding.value;
  const { handler, options, postpone } =
    typeof value === 'object' ? value : { handler: value, options: {}, postpone: null };
  if (postpone != null) {
    await postpone;
  }
  const observer = new IntersectionObserver(
    (entries: IntersectionObserverEntry[] = [], observer: IntersectionObserver) => {
      const _observe = elementWithObserve._observe?.[uid];
      if (!_observe) return; // Just in case, should never fire

      const isIntersecting = entries.some((entry) => entry.isIntersecting);

      // If is not quiet or has already been
      // initted, invoke the user callback
      if (
        handler &&
        (!modifiers.quiet || _observe.init) &&
        (!modifiers.once || isIntersecting || _observe.init)
      ) {
        handler(entries, observer, isIntersecting);
      }

      if (isIntersecting && modifiers.once) unbind(el, binding, vnode);
      else _observe.init = true;
    },
    options,
  );

  const observeObject = Object(elementWithObserve._observe);
  elementWithObserve._observe = observeObject;
  observeObject[uid] = { init: false, observer };

  observer.observe(el);
}

function unbind(el: HTMLElement, _binding: ObserveVNodeDirective, vnode: VNode) {
  const elementWithObserve = el as HTMLElementWithObserve;
  const uid = vnode.appContext?.app._uid ?? 0;

  const observeObject = elementWithObserve._observe;
  const observe = observeObject?.[uid];
  if (!observe) return;

  observe.observer.unobserve(el);
  delete observeObject[uid];
}

export const Intersect = {
  mounted: inserted,
  unmounted: unbind,
};

export default Intersect;
